// Apple Sign In helper. Routes to the native Capacitor plugin on iOS and to
// Apple's web JS SDK in a browser, behind a single signInWithApple() entry
// point. The returned shape (idToken + code) matches what POST /auth/apple
// expects in both cases — the backend's apple_auth.verify_id_token accepts
// either audience (Services ID for web, Bundle ID for native).

import { Capacitor } from '@capacitor/core';

export interface AppleSignInResult {
  idToken: string;
  code: string;
}

export async function signInWithApple(): Promise<AppleSignInResult> {
  if (Capacitor.isNativePlatform()) {
    return signInWithAppleNative();
  }
  return signInWithAppleWeb();
}

// --- Native (iOS via Capacitor) ---

async function signInWithAppleNative(): Promise<AppleSignInResult> {
  // Dynamic import so the web bundle doesn't pull in the native plugin code.
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

  // Native uses the app Bundle ID as the audience (the id_token's `aud`),
  // which must match APPLE_CLIENT_ID_IOS on the backend. We default to the
  // Bundle ID declared in capacitor.config.ts; an env override is provided
  // for future bundle-ID changes.
  const clientId = import.meta.env.VITE_APPLE_CLIENT_ID_IOS || 'app.fantasy.futbol';

  const result = await SignInWithApple.authorize({
    clientId,
    redirectURI: '', // ignored by the native flow
    scopes: 'email name',
  });

  const identityToken = result.response?.identityToken;
  const authorizationCode = result.response?.authorizationCode;
  if (!identityToken || !authorizationCode) {
    throw new Error('Apple sign-in returned no token.');
  }
  return { idToken: identityToken, code: authorizationCode };
}

// --- Web (browser via Apple's JS SDK) ---

const APPLE_SDK_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

interface AppleIDAuth {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state?: string;
    nonce?: string;
    usePopup: boolean;
  }): void;
  signIn(): Promise<{
    authorization: {
      id_token: string;
      code: string;
      state?: string;
    };
    user?: {
      email?: string;
      name?: { firstName?: string; lastName?: string };
    };
  }>;
}

declare global {
  interface Window {
    AppleID?: { auth: AppleIDAuth };
  }
}

let sdkLoadingPromise: Promise<void> | null = null;
let initialized = false;

function loadSdk(): Promise<void> {
  if (window.AppleID) return Promise.resolve();
  if (sdkLoadingPromise) return sdkLoadingPromise;

  sdkLoadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_SDK_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Apple SDK failed to load.')));
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_SDK_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Apple SDK failed to load.'));
    document.head.appendChild(script);
  });

  return sdkLoadingPromise;
}

function getClientId(): string {
  const clientId = import.meta.env.VITE_APPLE_CLIENT_ID_WEB;
  if (!clientId) {
    throw new Error(
      'Sign in with Apple is not configured. VITE_APPLE_CLIENT_ID_WEB is missing.',
    );
  }
  return clientId;
}

function getRedirectURI(): string {
  // Apple requires this URL be registered under the Services ID even when
  // usePopup: true (the popup doesn't navigate here). The origin must match
  // the registered domain.
  const origin = window.location.origin;
  return `${origin}/auth/apple/callback`;
}

async function ensureInitialized(): Promise<void> {
  await loadSdk();
  if (initialized || !window.AppleID) return;
  window.AppleID.auth.init({
    clientId: getClientId(),
    scope: 'email',
    redirectURI: getRedirectURI(),
    usePopup: true,
  });
  initialized = true;
}

async function signInWithAppleWeb(): Promise<AppleSignInResult> {
  await ensureInitialized();
  if (!window.AppleID) {
    throw new Error('Apple sign-in is not available.');
  }
  const response = await window.AppleID.auth.signIn();
  return {
    idToken: response.authorization.id_token,
    code: response.authorization.code,
  };
}
